'use client';
import React from 'react';
import { CountrySelect } from '../CountrySelect';
import { Modal } from '@components/Modal';
import Link from 'next/link';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'next/navigation';
import { hasCaptchaSiteKey } from '@utils/getCaptchaSiteKey';
import { LANGUAGE_OPTIONS } from '@components/IntlProvider/IntlProvider';
import { isValidPhoneNumber } from '@utils/validation.utils';

import { requestLeadGenerationAPI } from '@services/launches/requestLeadGeneration';
import SVGIcons from '@components/SVGIcon';
import { useLeadSourceParamsLaunchTeaser } from '@hooks/useLeadSourceParamsLaunchTeaser';
import { logEvent } from '@utils/googleAnalytics';
import { getCountryCodeAndNameFromValue } from '@services/properties/getLeadGenerationOptions';
import { NoTranslateWrapper } from '@components/NoTranslateWrapper';

import cn from '../style.module.scss';

type FormType = {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  contact: string;
  buyingReason: string;
  residentStatus: string;
  nationality: string;
  consent: boolean;
  responseKey?: string;
  comment?: string;
  receiveUpdates?: boolean;
};

const DEFAULT_FORM_VALUES = {
  countryCode: '+971___United Arab Emirates',
  buyingReason: '',
  consent: false,
  firstName: '',
  lastName: '',
  email: '',
  contact: '',
  residentStatus: '',
  nationality: '',
  responseKey: '',
  comment: '',
  receiveUpdates: false,
};

const DEFAULT_GA_KEYS = {
  category: 'Aldar Websites',
  trigger: 'Damascus',
};

export function LeadForm(props: {
  countryCodes: Record<string, string>[];
  nationalities: Record<string, string>[];
  t: { [key: string]: any };
}) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>(
    'idle'
  );
  const { lang } = useParams();
  const { getLeadSourceParamsLaunchTeaser } = useLeadSourceParamsLaunchTeaser();
  const { t } = props;
  const isFormStarted = React.useRef(false);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    trigger,
    watch,
    setError,
    unregister,
    reset,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver(schema),
  });

  const onSelectCountryCode = (value: string) => {
    setValue('countryCode', value);
    trigger('contact');
  };

  const handleCaptcha = React.useCallback((value: any) => {
    setValue('responseKey', value);
  }, []);

  const handleFormStart = () => {
    if (isFormStarted.current) return;
    isFormStarted.current = true;
    logEvent({
      event: 'form_start',
      additionalInfo: {
        form_id: 'art-house-launch-lead-form',
        form_type: 'art-house-launch-lead-form',
      },
    });
  };
  const onSubmitForm: SubmitHandler<FormType> = async (data) => {
    setStatus('loading');
    if (!data.consent) {
      setStatus('idle');
      setError('consent', {
        message:
          'Please agree to the Privacy Policy & Terms of Use to proceed.',
      });
      return;
    }

    try {
      const paramsKeys = getLeadSourceParamsLaunchTeaser();
      const response = await requestLeadGenerationAPI({
        ...data,
        ...paramsKeys,
        countryCode: getCountryCodeAndNameFromValue(data.countryCode).code,
        language: LANGUAGE_OPTIONS.find(({ value }) => value === lang)?.label,
        project: 'The Arthouse Saadiyat Grove',
      }).getResponse();

      logEvent({
        event: 'form_success',
        additionalInfo: {
          lead_id: response?.data?.leadId,
          form_id: 'art-house-launch-lead-form',
          form_type: 'art-house-launch-lead-form',
          Project_Name: 'The Arthouse Saadiyat Grove',
          Trigger: paramsKeys.enquiryTrigger ?? DEFAULT_GA_KEYS.trigger,
          Category: paramsKeys.enquiryCategory ?? DEFAULT_GA_KEYS.category,
        },
      });
      setStatus('success');
      if (hasCaptchaSiteKey()) {
        setValue('responseKey', '');
      }
    } catch (e: any) {
      setStatus('idle');
      setError('responseKey', {
        message: `${e.message ?? 'Lead generation failed!'} Please try again.`,
      });
      if (hasCaptchaSiteKey()) {
        setValue('responseKey', '');
      }
    }
  };

  const commonProps = { disabled: status === 'loading' ? true : false };

  React.useEffect(() => {
    if (!hasCaptchaSiteKey()) {
      unregister('responseKey');
    }
  }, []);
  const handleFormClose = () => {
    setStatus('idle');
    reset(undefined, { keepDefaultValues: true });
  };

  return (
    <>
      <div className={cn.launchContact} onBlur={handleFormStart}>
        <ul className={cn.contactList}>
          <li>
            <div className={cn.formsSides}>
              <div className={cn.formsSides__element}>
                <input
                  type="text"
                  id="fname"
                  placeholder={`${t['firstName']} *`}
                  {...register('firstName')}
                  {...commonProps}
                />
                <p className={cn.validationMessage}>
                  {errors.firstName?.message}
                </p>
              </div>
              <div className={cn.formsSides__element}>
                <input
                  type="text"
                  id="fname"
                  placeholder={`${t['lastName']} *`}
                  {...register('lastName')}
                  {...commonProps}
                />
                <p className={cn.validationMessage}>
                  {errors.lastName?.message}
                </p>
              </div>
            </div>
          </li>
          <li>
            <div className={cn.formsSides}>
              <div className={cn.formsSides__element}>
                <input
                  type="text"
                  id="fname"
                  placeholder={`${t['email']} *`}
                  {...register('email')}
                  {...commonProps}
                />
                <p className={cn.validationMessage}>{errors.email?.message}</p>
              </div>
              <div className={cn.formsSides__element}>
                <div className={cn.elementPhone}>
                  <div className={cn.phoneCountry}>
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <CountrySelect
                          {...field}
                          onChange={onSelectCountryCode}
                          options={props.countryCodes}
                          {...commonProps}
                        />
                      )}
                    />
                  </div>
                  <div
                    className={cn.phoneItem}
                    onBlur={() => trigger('contact')}
                  >
                    <input
                      type="tel"
                      placeholder={`${t['phoneNumber']} *`}
                      {...register('contact')}
                      {...commonProps}
                    />
                  </div>
                </div>
                <p className={cn.validationMessage}>
                  {errors.contact?.message}
                </p>
              </div>
            </div>
          </li>
          <li>
            <div className={cn.formsSides}>
              <div className={cn.formsSides__element}>
                <select
                  id="nationality"
                  defaultValue={''}
                  {...register('nationality')}
                  placeholder={`${t['nationality']} *`}
                  {...commonProps}
                >
                  <option value="" disabled>
                    {`${t['nationality']} *`}
                  </option>
                  {props.nationalities.map((nationality) => (
                    <NoTranslateWrapper key={nationality.value}>
                      <option key={nationality.value} value={nationality.value}>
                        {nationality.label}
                      </option>
                    </NoTranslateWrapper>
                  ))}
                </select>
                <p className={cn.validationMessage}>
                  {errors.nationality?.message}
                </p>
              </div>
              <div className={cn.formsSides__element}>
                <select
                  defaultValue={''}
                  {...register('residentStatus')}
                  placeholder={`${t['residencyStatus']} *`}
                  {...commonProps}
                >
                  <NoTranslateWrapper>
                    <option value="" disabled>
                      {`${t['residencyStatus']} *`}
                    </option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option value="Resident">{`${t['residencyStatusOptionResident']}`}</option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option value="Non Resident">
                      {`${t['residencyStatusOptionNonResident']}`}
                    </option>
                  </NoTranslateWrapper>
                </select>
                <p className={cn.validationMessage}>
                  {errors.residentStatus?.message}
                </p>
              </div>
            </div>
          </li>
          <li>
            <div className={cn.formsSides}>
              <div className={cn.formsSides__element}>
                <select
                  id="buyingReason"
                  defaultValue={''}
                  {...register('buyingReason')}
                  placeholder={`${t['reasonToBuy']} *`}
                  {...commonProps}
                >
                  <NoTranslateWrapper>
                    <option value="" disabled>
                      {`${t['reasonToBuy']} *`}
                    </option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option value="Investor">
                      {t['reasonBuyOptionInvestor']}
                    </option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option value="End User">
                      {t['reasonBuyOptionHomeOwner']}
                    </option>
                  </NoTranslateWrapper>
                </select>
                <p className={cn.validationMessage}>
                  {errors.buyingReason?.message}
                </p>
              </div>
            </div>
          </li>
          <li>
            <div className={cn.messageElement}>
              <textarea
                id="comment"
                {...register('comment')}
                placeholder={t['contactCommentsPlaceholder']}
              />
            </div>
          </li>

          <li>
            <div className={cn.authWrapper}>
              <div className={cn.formsAuth}>
                <label className={cn.customCheckbox}>
                  <input
                    type="checkbox"
                    id="vehicle1"
                    {...register('receiveUpdates')}
                    {...commonProps}
                  />
                  <span className={cn.checkMark}></span>
                </label>
                <p>{t['recieveUpdates']}</p>
              </div>
              <div className={cn.formsAuth}>
                <label className={cn.customCheckbox}>
                  <input
                    type="checkbox"
                    id="vehicle1"
                    // name='vehicle1'
                    value="true"
                    {...register('consent')}
                    {...commonProps}
                  />
                  <span className={cn.checkMark}></span>
                </label>
                <p>
                  <span>{t['consentLabel']} </span>
                  <Link
                    href={`https://www.aldar.com/${encodeURIComponent(
                      lang as string
                    )}/privacy-policy`}
                    target={'_blank'} rel="noopener noreferrer"
                  >
                    {t['privacyPolicy']}
                  </Link>
                  <span>{` & `}</span>
                  <Link
                    href={`https://www.aldar.com/${encodeURIComponent(
                      lang as string
                    )}/terms-and-conditions`}
                    target={'_blank'} rel="noopener noreferrer"
                  >
                    {t['termsAndConditions']}
                  </Link>
                  <span> {t['consentLabelEnd']}</span>
                </p>
              </div>
            </div>
          </li>
          <li>
            <div className={cn.reCaptcha}>
              <p className={cn.validationMessage}>
                {errors.responseKey?.message}
              </p>
              <p className={cn.validationMessage}>{errors.consent?.message}</p>
            </div>
          </li>
          <li>
            <div className={cn.formSubmit}>
              <button
                className={cn.registerButton}
                type="submit"
                onClick={handleSubmit(onSubmitForm)}
                {...commonProps}
              >
                {!commonProps.disabled ? t['actionButton'] : 'Loading...'}
              </button>
            </div>
          </li>
        </ul>
      </div>
      <Modal
        isOpen={status === 'success'}
        onClose={handleFormClose}
        className={cn.successModal}
      >
        <div data-notranslate className={cn.successMessage}>
          <div className={cn.closeBtn}>
            <span
              className={cn.closeBtn_icon}
              onClick={handleFormClose}
              data-testid="close-icon"
            >
              <SVGIcons type={'cross-svg'} />
            </span>
          </div>
          <div className={cn.messageInner}>
            <h2> {t['successHeading']}</h2>
            <div className={cn.messageInner__paragraph}>
              <p>{`${t['successContent']}`}</p>
            </div>
            <div className={cn.messageInner__button}>
              <a
                href="https://www.aldar.com/properties/en/communities"
                target="_blank" rel="noopener noreferrer"
                className={cn.messageButton}
              >
                {t['successButton']}
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

const schema = yup.object({
  firstName: yup
    .string()
    .required('First name is required')
    .max(35, 'First name should be 35 characters long')
    .matches(
      /^[A-Za-z\s]+$/,
      'First name should contain only letters and spaces'
    ),
  lastName: yup
    .string()
    .required('Last name is required')
    .max(35, 'Last name should be 35 characters long')
    .matches(
      /^[A-Za-z\s]+$/,
      'Last name should contain only letters and spaces'
    ),
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
  countryCode: yup.string().required(),
  contact: yup
    .string()
    .required('Phone number is required')
    .test(
      'Phone Number Validity Check',
      'Not a valid phone number',
      function (number: string) {
        const { code } = getCountryCodeAndNameFromValue(
          this.parent.countryCode
        );
        return isValidPhoneNumber(number, code);
      }
    )
    .test(
      'Phone Number Leading Zero validation check',
      'Please remove the leading 0 from your phone number.',
      function (number: string) {
        return !number.trim().startsWith('0');
      }
    )
    .matches(/^\d{1,20}$/, 'Phone number must be digits only')
    .trim(),
  buyingReason: yup.string().required('This field is required'),
  residentStatus: yup.string().required('This field is required'),
  nationality: yup.string().required('This field is required'),
  consent: yup.boolean().required(),
  receiveUpdates: yup.boolean().optional(),
  comment: yup.string(),
  responseKey: yup.string(),
});
